import React, { useState, useContext, useRef, useEffect } from 'react';
import FetchService from '../../services/fetchService';
import urlService from '../../services/urlService';
import useOutsideClick from '../../hooks/useOutsideClick';
import { mixpanelEventTrack } from '../../services/mixpanel';
import fieldParams from '../../const/fieldParams';
import BookerContext from '../../context/bookerContext';
import MapboxAutocomplete from '../MapboxAutocomplete';
import { useValidatedInput } from '../../hooks/useValidatedInput';
import { isMobile } from '../../utils/getMediaQuery';
import { ReactComponent as CurrentLocationIcon } from '../../assets/img/location-crosshairs.svg';
import '../../assets/styles/error.sass';
import BookerShortContext from '../../context/bookerShortContext';
import { simplifyAddress } from '../../utils/simplifyAddress';

const LocationInput = ({ fieldKey, getNewFare, formContainerRef }) => {
  const { key, label, placeholder } = fieldParams[fieldKey];
  const context = useContext(BookerContext);
  const { togglePopup } = useContext(BookerShortContext);
  const {
    state: {
      [fieldKey]: primary,
      [`${fieldKey === 'pickup' ? 'dropoff' : 'pickup'}`]: secondary,
      editor,
      editor: { address: { text = '' } = {} } = {},
      step,
      isWebBooker,
      fleets,
      paymentMethodId,
      userLocation,
    },
    updatePickup,
    updateDropoff,
    updateEditor,
    updateStep,
    updatePayment,
    updatePaymentMethodId,
    updateFleets,
    updateSelectedFleet,
  } = context;
  const { simple: { name, line1, city, borough, state, postal_code, logo_url } = {} } = primary || {};
  const address = primary ? `${line1}\r\n${borough ?? city}, ${state} ${postal_code}` : '';
  const [query, setQuery] = useState(address || text);
  const [txt, setTxt] = useState(!editor ? address : text);
  const [setIsValid] = useValidatedInput(!editor ? 'btn-view' : '', !!address?.length);
  const [editMode, setEditMode] = useState(false);
  const [geo, setGeo] = useState({});
  const viewBoxRef = useRef(null);
  const editBoxRef = useRef(null);
  const isMobileDevice = isMobile();
  const displayLocationLink = (isWebBooker && step === 5) || (!isWebBooker && step === 3);

  useEffect(() => {
    setQuery(!editor ? address : text);
    setTxt(!editor ? address : address || text);
  }, [editMode]);

  useEffect(() => {
    const { simple: { latitude, longitude } = {} } = secondary || primary || {};
    setGeo({ latitude, longitude });
  }, [primary, secondary]);

  useOutsideClick(
    isMobileDevice ? editBoxRef : formContainerRef,
    (event, { isComponentVisible = false }) => {
      if (!isComponentVisible || (isMobileDevice ? !editBoxRef.current : !formContainerRef.current)) return;

      const root = document.documentElement;
      if (editMode) {
        root?.style.removeProperty('--form-container-extra-space');
        root?.style.removeProperty('--location-input-dropdown-max-height');
        root?.style.removeProperty('--location-input-dropdown-min-height');
      }
      if (!txt && primary) {
        if (key === 'pickup') updatePickup('');
        else updateDropoff('');
      }
      setEditMode(false);
    },
    { isComponentVisible: editMode || editor || false },
  );

  const getGeo = () => {
    const { simple: { latitude, longitude } = {} } = primary || secondary || {};
    setGeo({ latitude, longitude });
  };

  const onClickHandler = () => {
    if (key === 'pickup') {
      switch (step) {
        case 1:
          mixpanelEventTrack('editPickupGetCode');
          break;
        case 3:
          mixpanelEventTrack('editPickupPayment');
          break;
        case 5:
          mixpanelEventTrack(isWebBooker ? 'editPickupRequestRideWebBook' : 'editPickupRequestRide');
          break;
        case 8:
          mixpanelEventTrack('editPickupRequestRideWebBook');
          break;
        default:
          break;
      }
    }
    if (key === 'dropoff') {
      switch (step) {
        case 5:
          mixpanelEventTrack(isWebBooker ? 'editDestinationRequestRideWebBook' : 'editDestinationRequestRide');
          break;
        case 8:
          mixpanelEventTrack('editDestinationRequestRideWebBook');
          break;
        default:
          break;
      }
    }

    getGeo();

    if ((isWebBooker && step === 10) || (!isWebBooker && step === 7)) {
      if (viewBoxRef.current) {
        const parent = viewBoxRef.current.offsetParent;
        const currentPosition = parent?.offsetTop;
        const children = parent?.offsetParent.children;
        const header = Array.from(children).find(ch => ch.className.includes('header'));
        const headerHeight = header?.clientHeight;
        const headerOffset = header?.offsetTop;
        // Accessing sass variable '--location-input-offset-top' using plain JavaScript
        // and changing according to the current location view box vertical position
        const root = document.documentElement;
        root?.style.setProperty('--location-input-offset-top', `${currentPosition - headerHeight - headerOffset}px`);
      }
      updateEditor({ key, address: primary });
    } else if (isWebBooker && step !== 5) {
      updateStep(5);
      setTimeout(() => {
        const viewBoxClicked = document.querySelector(`.form-view-box.${key}`);
        viewBoxClicked?.click();
      }, 100);
    } else setEditMode(true);
  };

  const onKeyUp = e => {
    const { value } = e.target;
    if (!value) {
      setQuery('');
      setTxt('');
      return;
    }
    setQuery(value);
    setTxt(value);
  };

  const onClickLocationHandler = e => {
    if (e.target.className === 'current-location-link') {
      mixpanelEventTrack('currentLocationPickupLink');
    } else {
      mixpanelEventTrack('currentLocationPickup');
    }

    if (!userLocation || !userLocation?.latitude) {
      mixpanelEventTrack('currentLocationNotEnabled');
      togglePopup(true, 'userLocation');
      return;
    }
    e.stopPropagation();

    FetchService.getMapboxResource(urlService.reverseGeocoding())
      .then(response => {
        const { text, simple } = simplifyAddress(
          {
            text: response.features[0].place_name,
            latitude: response.features[0].center[1],
            longitude: response.features[0].center[0],
            isReversing: true,
          },
          response.features[0],
        );

        const address = { text, simple };
        mixpanelEventTrack('pickupPopulated');
        updatePickup(address);
      })
      .catch(error => {
        console.error('No User Location');
      });
  };

  useEffect(() => {
    if (!(editMode || editor || editBoxRef.current)) return;

    editBoxRef.current?.select();
  }, [editor, editMode]);

  const suggestionSelect = ({ text, simple }) => {
    const address = { text, simple };
    setQuery(text);
    if (key === 'pickup') {
      mixpanelEventTrack('pickupPopulated');
      updatePickup(address);
    }
    if (key === 'dropoff') {
      mixpanelEventTrack('dropoffPopulated');
      if ((isWebBooker && step === 10) || (!isWebBooker && step === 7)) getNewFare(address);
      else updateDropoff(address);
    }
    updateEditor('');
    setEditMode(false);
    setIsValid(!!text);

    if (fleets) {
      if (paymentMethodId) {
        updatePayment({});
        updatePaymentMethodId(null);
      }
      updateFleets(null);
      updateSelectedFleet(null);
    }
  };

  const logoStyle = {
    '--logoImage': `url(${logo_url})`,
  };

  return (
    <div className={`form-group form-group-${key} ${!displayLocationLink ? 'pickupLabelLink' : ''}`}>
      {key === 'pickup' && displayLocationLink && (
        <span className="current-location-link" onClick={onClickLocationHandler}>
          Use my current location
        </span>
      )}
      <label htmlFor={key} className={`form-label${txt ? '' : ' hidden'}`}>
        {label}
      </label>
      {!(editor || editMode) ? (
        <div
          ref={viewBoxRef}
          className={`form-view-box read-only ${key}`}
          onClick={onClickHandler}
          {...(logo_url && { 'data-logo': logo_url })}
          {...(logo_url && { style: logoStyle })}
        >
          {name && <div className="location-alias">{name.replace('_', ' ')}</div>}
          <div className="address-current-location-container">
            {key === 'pickup' && <CurrentLocationIcon className="current-location" onClick={onClickLocationHandler} />}
            {primary ? (
              <div className="address-container">
                <div className="address address-full">{line1}</div>
                <div className="address address-short">
                  {borough ? `${borough}` : `${city}`}, {state} {postal_code}
                </div>
              </div>
            ) : (
              <div className="placeholder">{placeholder}</div>
            )}
          </div>
        </div>
      ) : (
        <MapboxAutocomplete
          inputId={key}
          inputClass={`search ${key}${!query.length ? ' no-query' : ''}`}
          onSuggestionSelect={suggestionSelect}
          country="us"
          query={query}
          inputOnKeyUp={onKeyUp}
          inputRef={editBoxRef}
          formContainerRef={formContainerRef}
          {...(placeholder && { placeholder: placeholder })}
          geo={geo}
        />
      )}
    </div>
  );
};

export default LocationInput;
